import { useCallback, useRef, useState, useEffect } from 'react';
import { useChainId } from 'wagmi';

export type ResourceType = 'slap' | 'sticker';
export type ResourceFormat = 'og' | 'fc' | 'sq';

export interface ResourceCheckResult {
  status: 'loading' | 'available' | 'error' | 'timeout';
  httpStatus?: number;
  redirectUrl?: string;
  error?: string;
  attempts: number;
  totalTimeElapsed: number;
}

export interface UseServerResourceCheckOptions {
  resourceType: ResourceType;
  resourceId: number;
  format?: ResourceFormat;
  enabled?: boolean;
  onComplete?: (result: ResourceCheckResult) => void;
  onError?: (result: ResourceCheckResult) => void;
}

export interface UseServerResourceCheckReturn {
  status: 'loading' | 'available' | 'error' | 'timeout' | null;
  result: ResourceCheckResult | null;
  redirectUrl: string | null;
  error: string | null;
  attempts: number;
  totalTimeElapsed: number;
  isPolling: boolean;
  startPolling: () => void;
  stopPolling: () => void;
}

// Global state to track which resources are already being polled
const globalPollingTracker = new Map<string, boolean>();

/**
 * React hook for checking blockchain resource availability via server-side API
 * Polls by making repeated fast API calls to /api/resource-check
 */
export function useServerResourceCheck(
  options: UseServerResourceCheckOptions
): UseServerResourceCheckReturn {
  const {
    resourceType,
    resourceId,
    format = 'og',
    enabled = true,
    onComplete,
    onError
  } = options;

  const chainId = useChainId();
  const [result, setResult] = useState<ResourceCheckResult | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [hasCompletedFirstSync, setHasCompletedFirstSync] = useState(false);
  const startTimeRef = useRef<number>(0);
  const attemptsRef = useRef<number>(0);
  const isPollingRef = useRef<boolean>(false);
  
  // Create a unique key for this resource
  const resourceKey = `${chainId}-${resourceType}-${resourceId}-${format}`;

  /**
   * Make a single resource check via the API
   */
  const checkResource = useCallback(async (): Promise<ResourceCheckResult> => {
    attemptsRef.current++;
    
    console.log(
      '🔍 Client polling API for',
      resourceType,
      resourceId,
      '(attempt',
      attemptsRef.current,
      ')'
    );
    
    try {
      const response = await fetch('/api/resource-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resourceType,
          resourceId,
          chainId,
          format
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const totalTimeElapsed = Date.now() - startTimeRef.current;
      
      const result: ResourceCheckResult = {
        status: data.status,
        httpStatus: data.httpStatus,
        redirectUrl: data.redirectUrl,
        error: data.error,
        attempts: attemptsRef.current,
        totalTimeElapsed
      };

      console.log(
        '📊 Client received API result:',
        data.status,
        `(${data.httpStatus}) after`,
        totalTimeElapsed,
        'ms'
      );
      
      return result;
      
    } catch (error) {
      const totalTimeElapsed = Date.now() - startTimeRef.current;
      console.error(
        '💥 Client API check failed on attempt',
        attemptsRef.current,
        ':',
        error
      );
      
      return {
        status: 'error',
        error: error instanceof Error ? error.message : 'API request failed',
        attempts: attemptsRef.current,
        totalTimeElapsed
      };
    }
  }, [resourceType, resourceId, chainId, format]);

  /**
   * Start client-side polling with the same schedule as original ResourceLocator
   */
  const startPolling = useCallback(async () => {
    console.log('🎯 startPolling called for', resourceType, resourceId);
    
    // Don't start polling if already completed first sync or currently
    // polling or globally being polled
    if (
      isPolling ||
      !enabled ||
      hasCompletedFirstSync ||
      globalPollingTracker.get(resourceKey)
    ) {
      if (hasCompletedFirstSync) {
        console.log(
          '📋 Skipping polling for',
          resourceType,
          resourceId,
          '- already completed first sync'
        );
      } else if (globalPollingTracker.get(resourceKey)) {
        console.log(
          '📋 Skipping polling for',
          resourceType,
          resourceId,
          '- already being polled globally'
        );
      } else if (isPolling) {
        console.log(
          '📋 Skipping polling for',
          resourceType,
          resourceId,
          '- already polling'
        );
      } else if (!enabled) {
        console.log(
          '📋 Skipping polling for',
          resourceType,
          resourceId,
          '- not enabled'
        );
      }
      return;
    }
    
    // Mark as globally being polled
    globalPollingTracker.set(resourceKey, true);
    
    console.log(
      '🚀 Starting client-side API polling for',
      resourceType,
      resourceId
    );
    
    setIsPolling(true);
    isPollingRef.current = true;
    setResult(null);
    startTimeRef.current = Date.now();
    attemptsRef.current = 0;

    const poll = async (phase: number, maxAttempts: number, intervalMs: number) => {
      console.log(
        '📍 Phase',
        phase,
        ': Checking every',
        intervalMs / 1000,
        'seconds (up to',
        maxAttempts,
        'attempts)'
      );
      
      for (let i = 0; i < maxAttempts; i++) {
        console.log(
          '🔄 Phase',
          phase,
          'iteration',
          i + 1,
          '/',
          maxAttempts,
          ', isPollingRef=',
          isPollingRef.current
        );
        if (!isPollingRef.current) {
          console.log(`🛑 Breaking out of polling loop - isPollingRef is false`);
          break; // Stop if polling was cancelled
        }
        
        const result = await checkResource();
        setResult(result);
        
        // Stop immediately on first 202 (loading) response
        if (result.status === 'loading' && result.httpStatus === 202) {
          console.log(
            '🎯 First 202 received via API - triggering sync complete for',
            resourceType,
            resourceId
          );
          setIsPolling(false);
          isPollingRef.current = false;
          setHasCompletedFirstSync(true);
          globalPollingTracker.delete(resourceKey);
          onComplete?.(result);
          return;
        }
        
        // Stop on available or error
        if (result.status === 'available' || result.status === 'error') {
          setIsPolling(false);
          isPollingRef.current = false;
          globalPollingTracker.delete(resourceKey);
          
          if (result.status === 'available') {
            onComplete?.(result);
          } else {
            onError?.(result);
          }
          return;
        }
        
        // Wait before next attempt (except for last attempt of phase)
        if (i < maxAttempts - 1) {
          await new Promise(resolve => {
            pollingTimeoutRef.current = setTimeout(resolve, intervalMs);
          });
        }
      }
    };

    try {
      // Fast start phase: Every 0.5 seconds for 1.5 seconds (3 attempts)
      await poll(0, 3, 500);
      
      if (!isPollingRef.current) return; // Stopped early

      // Phase 1: Every 2 seconds for 10 seconds (5 attempts)
      await poll(1, 5, 2000);
      
      if (!isPollingRef.current) return; // Stopped early
      
      // Phase 2: Every 3 seconds for 21 seconds (7 attempts)  
      await poll(2, 7, 3000);
      
      if (!isPollingRef.current) return; // Stopped early
      
      // Phase 3: Every 4 seconds for 50 seconds (12-13 attempts)
      await poll(3, 13, 4000);
      
      if (!isPollingRef.current) return; // Stopped early
      
      // If we get here, we've exceeded retry limits
      const totalTimeElapsed = Date.now() - startTimeRef.current;
      console.log(
        '⏰ Client API polling timeout after',
        attemptsRef.current,
        'attempts and',
        totalTimeElapsed,
        'ms'
      );
      
      const timeoutResult: ResourceCheckResult = {
        status: 'timeout',
        error: 'Resource did not become available within timeout period',
        attempts: attemptsRef.current,
        totalTimeElapsed
      };
      
      setResult(timeoutResult);
      setIsPolling(false);
      isPollingRef.current = false;
      globalPollingTracker.delete(resourceKey);
      onError?.(timeoutResult);
      
    } catch (error) {
      const totalTimeElapsed = Date.now() - startTimeRef.current;
      console.log(
        '💥 Client API polling error:',
        error instanceof Error ? error.message : 'Unknown error'
      );
      
      const errorResult: ResourceCheckResult = {
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        attempts: attemptsRef.current,
        totalTimeElapsed
      };
      
      setResult(errorResult);
      setIsPolling(false);
      isPollingRef.current = false;
      globalPollingTracker.delete(resourceKey);
      onError?.(errorResult);
    }
  }, [
    resourceType,
    resourceId,
    enabled,
    isPolling,
    hasCompletedFirstSync,
    resourceKey,
    onComplete,
    onError,
    checkResource
  ]);

  const stopPolling = useCallback(() => {
    console.log(`🛑 Stopping client API polling for ${resourceType} ${resourceId}`);
    
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    
    setIsPolling(false);
    isPollingRef.current = false;
    globalPollingTracker.delete(resourceKey);
  }, [resourceType, resourceId, resourceKey]);

  // Auto-start polling when enabled and dependencies change
  useEffect(() => {
    console.log(
      '🔍 useServerResourceCheck useEffect: enabled=',
      enabled,
      ', isPolling=',
      isPolling,
      ', hasCompletedFirstSync=',
      hasCompletedFirstSync,
      ', resourceId=',
      resourceId
    );
    if (enabled && !isPolling && !hasCompletedFirstSync && resourceId > 0) {
      console.log('✅ Auto-starting polling for', resourceType, resourceId);
      startPolling();
    }
  }, [
    enabled,
    isPolling,
    hasCompletedFirstSync,
    resourceId,
    startPolling,
    resourceType
  ]);

  // Cleanup on unmount - only cleanup, don't stop active polling
  useEffect(() => {
    return () => {
      // Only clean up timeouts and global tracker, don't stop active polling
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      console.log(`🧹 Cleaning up polling tracker for ${resourceType} ${resourceId}`);
    };
  }, [resourceType, resourceId]);

  return {
    status: result?.status || null,
    result,
    redirectUrl: result?.redirectUrl || null,
    error: result?.error || null,
    attempts: result?.attempts || 0,
    totalTimeElapsed: result?.totalTimeElapsed || 0,
    isPolling,
    startPolling,
    stopPolling
  };
}

/**
 * Convenience hook specifically for slap resources
 */
export function useServerSlapResourceCheck(
  slapId: number,
  options: Omit<UseServerResourceCheckOptions, 'resourceType' | 'resourceId'> = {}
): UseServerResourceCheckReturn {
  return useServerResourceCheck({
    resourceType: 'slap',
    resourceId: slapId,
    ...options
  });
}

/**
 * Convenience hook specifically for sticker resources
 */
export function useServerStickerResourceCheck(
  stickerId: number,
  options: Omit<UseServerResourceCheckOptions, 'resourceType' | 'resourceId'> = {}
): UseServerResourceCheckReturn {
  return useServerResourceCheck({
    resourceType: 'sticker',
    resourceId: stickerId,
    ...options
  });
}
